/**
 * FmRouteCatalogDefault — one truthful navigation contract.
 *
 * Top-level entries are owner jobs, not route names. A job either opens a real
 * menu or navigates to its one real workspace; the Assistant is the right-side
 * launcher of the routes that serve one, never a job tab. Routes
 * that require a selected property declare that requirement explicitly so the
 * shell can hand the owner to the governed property chooser.
 */

import { appRoutePatterns } from '../navigation/appRoutePatterns';
import type { FmRouteCatalogData } from './FmRouteCatalog';

export const fmRouteCatalogDefault = {
  routes: [
    {
      id: 'my-forests',
      path: appRoutePatterns.myForests,
      label: 'My forests',
      description: 'Choose a forest or see which one needs attention',
      icon: 'home',
      primaryNavId: 'my-forests',
      groupId: 'forest-spaces',
      scope: 'global',
      requiredContext: 'none',
      placement: 'menu',
      availability: 'active',
      implementReturn: true,
    },
    {
      id: 'browse-properties',
      path: appRoutePatterns.browseProperties,
      label: 'Find or add a forest',
      description: 'Search for another property and add it to My forests',
      icon: 'search',
      primaryNavId: 'my-forests',
      groupId: 'forest-spaces',
      scope: 'global',
      requiredContext: 'none',
      placement: 'menu',
      availability: 'active',
    },
    {
      id: 'map',
      path: appRoutePatterns.map,
      label: 'Forest map',
      description: 'Find your forests and inspect stands spatially',
      icon: 'map',
      primaryNavId: 'my-forests',
      groupId: 'forest-spaces',
      scope: 'global',
      requiredContext: 'none',
      placement: 'menu',
      availability: 'active',
      ownsSubtree: false,
      implementReturn: true,
      selection: {
        title: 'Choose a forest to show on the map',
        description: 'Select the forest you want the map to refocus on.',
        actionLabel: 'Show forest on map',
      },
    },
    {
      id: 'forest-overview',
      path: appRoutePatterns.forestOverview,
      label: 'Forest overview',
      description: 'Understand what the selected forest contains',
      icon: 'bar-chart',
      primaryNavId: 'understand',
      groupId: 'selected-forest',
      scope: 'property',
      requiredContext: 'property',
      placement: 'menu',
      availability: 'active',
      implementReturn: true,
      selection: {
        title: 'Choose a forest to review',
        description: 'Select the forest whose overview you want to open.',
        actionLabel: 'Open forest overview',
      },
    },
    {
      id: 'stand-detail',
      path: appRoutePatterns.standDetail,
      label: 'Stand detail',
      description: 'Inspect a stand selected inside a forest workspace',
      icon: 'bar-chart',
      primaryNavId: 'understand',
      scope: 'property',
      requiredContext: 'property',
      placement: 'contextual',
      activeMenuRouteId: 'forest-overview',
      propertySwitchRouteId: 'forest-overview',
      availability: 'active',
      implementReturn: true,
    },
    {
      id: 'measurement-property',
      path: appRoutePatterns.measurementProperty,
      label: 'Measurements for this forest',
      description: 'Compare campaigns and measured evidence for the selected forest',
      icon: 'ruler',
      primaryNavId: 'understand',
      groupId: 'selected-forest',
      scope: 'property',
      requiredContext: 'property',
      placement: 'menu',
      availability: 'active',
      implementReturn: true,
      selection: {
        title: 'Choose a forest’s measurements',
        description: 'Select the forest whose measured evidence you want to inspect.',
        actionLabel: 'Open forest measurements',
      },
    },
    {
      id: 'measurements',
      path: appRoutePatterns.measurements,
      label: 'Manage measurement campaigns',
      description: 'Find, import, and manage measurements across all forests',
      icon: 'ruler',
      primaryNavId: 'understand',
      groupId: 'all-forests',
      scope: 'global',
      requiredContext: 'none',
      placement: 'menu',
      availability: 'active',
      ownsSubtree: false,
      implementReturn: true,
      propertyContext: {
        source: 'query',
        parameter: 'property',
        requirement: 'optional',
        requiredWithParameters: ['mode', 'stage', 'stand'],
        requiredWithParameterValues: {
          dialog: ['measurement-upload', 'measurement-manual'],
        },
      },
      propertySwitchRouteId: 'measurement-property',
    },
    {
      id: 'measurement-campaign',
      path: appRoutePatterns.measurementCampaign,
      label: 'Measurement campaign',
      description: 'Inspect a campaign selected in campaign management',
      icon: 'ruler',
      primaryNavId: 'understand',
      scope: 'contextual',
      requiredContext: 'none',
      placement: 'contextual',
      activeMenuRouteId: 'measurements',
      propertyContext: {
        source: 'query',
        parameter: 'property',
        requirement: 'optional',
      },
      propertySwitchRouteId: 'measurement-property',
      availability: 'active',
      ownsSubtree: false,
      implementReturn: true,
    },
    {
      id: 'measurement-plot',
      path: appRoutePatterns.measurementPlot,
      label: 'Measurement plot',
      description: 'Inspect a plot selected inside a measurement campaign',
      icon: 'ruler',
      primaryNavId: 'understand',
      scope: 'contextual',
      requiredContext: 'none',
      placement: 'contextual',
      activeMenuRouteId: 'measurements',
      propertyContext: {
        source: 'query',
        parameter: 'property',
        requirement: 'required',
      },
      propertySwitchRouteId: 'measurement-property',
      availability: 'active',
      implementReturn: true,
    },
    {
      id: 'price-estimate',
      path: appRoutePatterns.priceEstimate,
      label: 'Price estimate',
      description: 'Understand the selected forest’s current value',
      icon: 'dollar',
      primaryNavId: 'understand',
      groupId: 'selected-forest',
      scope: 'property',
      requiredContext: 'property',
      placement: 'menu',
      availability: 'active',
      implementReturn: true,
      selection: {
        title: 'Choose a forest to value',
        description: 'Select the forest whose current value you want to estimate.',
        actionLabel: 'Open price estimate',
      },
    },
    {
      id: 'operations-planning',
      path: appRoutePatterns.operationsPlanning,
      label: 'Optimize this forest',
      description: 'Compare management plans and choose the best course',
      icon: 'project',
      primaryNavId: 'optimize',
      scope: 'property',
      requiredContext: 'property',
      placement: 'menu',
      availability: 'active',
      implementReturn: true,
      selection: {
        title: 'Choose a forest to optimize',
        description: 'Select the forest whose management alternatives you want to compare.',
        actionLabel: 'Open optimization',
      },
    },
    {
      id: 'implement',
      path: appRoutePatterns.implement,
      label: 'Tender the harvest',
      description: 'Choose the plan work and create a call for tenders',
      icon: 'check-circle',
      primaryNavId: 'implement',
      groupId: 'timber-sale',
      scope: 'property',
      requiredContext: 'property',
      placement: 'menu',
      availability: 'active',
      selection: {
        title: 'Choose a forest to tender',
        description: 'Select the forest whose planned work you want to put out to tender.',
        actionLabel: 'Open tendering',
      },
    },
    {
      id: 'offer-decode',
      path: appRoutePatterns.offerDecode,
      label: 'Evaluate offers',
      description: 'Read received timber offers and compare them on your own stands',
      icon: 'check-circle',
      primaryNavId: 'implement',
      groupId: 'timber-sale',
      scope: 'property',
      requiredContext: 'property',
      placement: 'menu',
      availability: 'active',
      selection: {
        title: 'Choose a forest whose offers you evaluate',
        description: 'Select the forest whose stands the received timber offers are compared on.',
        actionLabel: 'Evaluate offers',
      },
    },
    {
      id: 'offer-decode-result',
      path: appRoutePatterns.offerDecodeResult,
      label: 'Offer analysis',
      description: 'Read a delivered comparison of received timber offers',
      icon: 'check-circle',
      primaryNavId: 'implement',
      scope: 'property',
      requiredContext: 'property',
      placement: 'contextual',
      activeMenuRouteId: 'offer-decode',
      propertySwitchRouteId: 'offer-decode',
      availability: 'active',
    },
    {
      id: 'watch',
      path: appRoutePatterns.watch,
      label: 'Alerts',
      description: 'See whether a watched forest needs attention',
      icon: 'monitor',
      primaryNavId: 'watch',
      groupId: 'watch-signals',
      scope: 'global',
      requiredContext: 'none',
      placement: 'menu',
      availability: 'active',
      implementReturn: true,
      propertyContext: {
        source: 'query',
        parameter: 'property',
        requirement: 'optional',
      },
      selection: {
        title: 'Choose a forest to watch',
        description: 'Select the forest whose monitoring signals you want to review.',
        actionLabel: 'Open alerts',
      },
    },
    {
      id: 'satellite-history',
      path: appRoutePatterns.satelliteHistory,
      label: 'Satellite history',
      description: 'Investigate dated Sentinel-2 frames for a forest',
      icon: 'monitor',
      primaryNavId: 'watch',
      groupId: 'watch-signals',
      scope: 'global',
      requiredContext: 'none',
      placement: 'menu',
      availability: 'active',
      propertyContext: {
        source: 'query',
        parameter: 'property',
        requirement: 'optional',
      },
      selection: {
        title: 'Choose a forest’s satellite history',
        description: 'Select the forest whose dated satellite frames you want to inspect.',
        actionLabel: 'Open satellite history',
      },
    },
    {
      id: 'climate-history',
      path: appRoutePatterns.climateHistory,
      label: 'Climate history',
      description: 'Compare observed weather around a forest with the 1991–2020 normal',
      icon: 'monitor',
      primaryNavId: 'watch',
      groupId: 'watch-signals',
      scope: 'global',
      requiredContext: 'none',
      placement: 'menu',
      availability: 'active',
      propertyContext: {
        source: 'query',
        parameter: 'property',
        requirement: 'optional',
      },
      selection: {
        title: 'Choose a forest’s climate history',
        description: 'Select the forest whose observed weather you want to compare with the normal.',
        actionLabel: 'Open climate history',
      },
    },
    {
      id: 'buyer-diligence',
      path: appRoutePatterns.buyerDiligence,
      label: 'Buyer Diligence',
      description: 'Read the public parcel record, its limits, and investigation links',
      icon: 'search',
      primaryNavId: 'my-forests',
      scope: 'contextual',
      requiredContext: 'none',
      placement: 'contextual',
      activeMenuRouteId: 'browse-properties',
      propertyContext: {
        source: 'query',
        parameter: 'property',
        requirement: 'optional',
      },
      propertySwitchRouteId: 'map',
      availability: 'active',
    },
    {
      id: 'timber-prices',
      path: appRoutePatterns.timberPrices,
      label: 'Timber prices',
      description: 'Inspect the dated market basis behind a value',
      icon: 'dollar',
      primaryNavId: 'understand',
      groupId: 'all-forests',
      scope: 'global',
      requiredContext: 'none',
      placement: 'menu',
      availability: 'active',
      implementReturn: true,
      propertyContext: {
        source: 'query',
        parameter: 'property_id',
        requirement: 'optional',
      },
      selection: {
        title: 'Choose a forest’s timber prices',
        description: 'Select the forest whose regional timber price basis you want to inspect.',
        actionLabel: 'Open timber prices',
      },
    },
    {
      id: 'settings',
      path: appRoutePatterns.settings,
      label: 'Settings',
      description: 'Manage account and application settings',
      icon: 'settings',
      primaryNavId: 'account',
      scope: 'global',
      requiredContext: 'none',
      placement: 'utility',
      availability: 'active',
    },
    {
      id: 'about',
      path: appRoutePatterns.about,
      label: 'About the service',
      description: 'What Lehti AI is and where its data comes from',
      icon: 'info',
      primaryNavId: 'account',
      scope: 'global',
      requiredContext: 'none',
      placement: 'utility',
      availability: 'active',
    },
  ],

  groups: [
    {
      id: 'forest-spaces',
      label: 'Your forests',
      description: 'Choose a forest, open the map, or add another property',
      routeIds: ['my-forests', 'map', 'browse-properties'],
    },
    {
      id: 'selected-forest',
      label: 'Selected forest',
      description: 'Understand this forest, its measured evidence, and its value',
      routeIds: ['forest-overview', 'price-estimate', 'measurement-property'],
    },
    {
      id: 'all-forests',
      label: 'All forests',
      description: 'Market prices and measurement campaigns across property boundaries',
      routeIds: ['timber-prices', 'measurements'],
    },
    {
      id: 'timber-sale',
      label: 'Timber sale',
      description: 'Put the planned harvest out to tender or evaluate the offers you received',
      routeIds: ['implement', 'offer-decode'],
    },
    {
      id: 'watch-signals',
      label: 'Signals',
      description: 'Alerts, satellite frames and weather for the selected forest',
      routeIds: ['watch', 'satellite-history', 'climate-history'],
    },
  ],

  topJobs: [
    {
      id: 'my-forests',
      label: 'My forests',
      icon: 'home',
      desktopPlacement: 'primary',
      activation: { kind: 'menu' },
      groupIds: ['forest-spaces'],
    },
    {
      id: 'understand',
      label: 'Understand',
      icon: 'bar-chart',
      desktopPlacement: 'primary',
      activation: { kind: 'menu' },
      groupIds: ['selected-forest', 'all-forests'],
    },
    {
      id: 'optimize',
      label: 'Optimize',
      icon: 'project',
      desktopPlacement: 'primary',
      activation: { kind: 'route', routeId: 'operations-planning' },
      groupIds: [],
    },
    {
      id: 'implement',
      label: 'Timber sale',
      icon: 'check-circle',
      desktopPlacement: 'primary',
      activation: { kind: 'menu' },
      groupIds: ['timber-sale'],
    },
    {
      id: 'watch',
      label: 'Watch',
      icon: 'monitor',
      desktopPlacement: 'primary',
      activation: { kind: 'menu' },
      groupIds: ['watch-signals'],
    },
  ],

  promoCards: [],
} satisfies FmRouteCatalogData;
